import React, { useEffect, useState } from 'react';

interface CustomToggleButtonProperties {
  labels: (string | React.ReactNode)[] | undefined;
  icons?: JSX.Element[];
  activeIcons?: JSX.Element[];
  onToggle?: (index: number, label: any) => void;
  defaultActiveIndex?: number;
  // eslint-disable-next-line react/require-default-props
  activeIndex?: number;
  type?: 'chart';
  version?: string;
  hoverIcons?: JSX.Element[];
}

/**
 * Custom toggle button component
 * @param {any} properties Component properties
 * @returns {any} custom toggle button
 */
function CustomToggleButton(properties: CustomToggleButtonProperties) {
  const {
    labels,
    icons,
    activeIcons,
    onToggle,
    defaultActiveIndex = 0,
    activeIndex,
    type,
    version,
    hoverIcons,
  } = properties;

  // fallback to internal state if activeIndex is NOT provided
  const [internalIndex, setInternalIndex] = useState(defaultActiveIndex);

  // this is the real active index
  const currentActive = activeIndex !== undefined ? activeIndex : internalIndex;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  /**
   * Function for handling toggle
   * @param {any} index index number
   * @param {any} label label element
   */
  const handleToggle = (index: number, label: any) => {
    // only update internal state in UNCONTROLLED mode
    if (activeIndex === undefined) {
      setInternalIndex(index);
    }
    onToggle?.(index, label);
  };

  // update internal when defaultActiveIndex changes (UNCONTROLLED mode only)
  useEffect(() => {
    if (activeIndex === undefined) {
      setInternalIndex(defaultActiveIndex);
    }
  }, [defaultActiveIndex, activeIndex]);

  /**
   * Rendering icon
   * @param {any} index index number
   * @returns {any} icon element
   */
  const renderIcon = (index: number) => {
    if (currentActive === index && activeIcons?.[index]) {
      return <span className="icon active-icon">{activeIcons[index]}</span>;
    }

    if (hoverIndex === index && hoverIcons?.[index]) {
      return <span className="icon hover-icon">{hoverIcons[index]}</span>;
    }

    if (icons?.[index]) {
      return <span className="icon">{icons[index]}</span>;
    }

    return null;
  };

  return (
    <div className={`toggle-button-${version} ${type}`}>
      {labels?.map((label, index) => (
        <div
          key={index}
          className={`btn ${currentActive === index ? 'active' : ''}`}
          onClick={() => handleToggle(index, label)}
          onMouseEnter={() => setHoverIndex(index)}
          onMouseLeave={() => setHoverIndex(null)}
          role="presentation">
          {renderIcon(index)}
          {label}
        </div>
      ))}
      <span className="toggle-button-border"> </span>
    </div>
  );
}

CustomToggleButton.defaultProps = {
  defaultActiveIndex: 0,
  icons: undefined,
  activeIcons: undefined,
  onToggle: undefined,
  type: '',
  version: 'v1',
  hoverIcons: undefined,
};

export default CustomToggleButton;
